import type { MrtColumnFilterVariant, MrtFilterOperator, StoredFilterValue } from '../mrtCollectionTypes'

export const DEFAULT_FILTER_OPERATOR: MrtFilterOperator = 'is'

// Compact labels for the operator dropdown.
export const FILTER_OPERATOR_INTL_ID: Record<MrtFilterOperator, string> = {
  is: 'filters.is',
  isNot: 'filters.negatable.isNot',
  empty: 'filters.empty',
  notEmpty: 'filters.notEmpty',
}

// Sentence-form labels for the filter chip, which reads "Column: <operator> <value>".
export const FILTER_OPERATOR_CHIP_INTL_ID: Record<MrtFilterOperator, string> = {
  is: 'filters.is',
  isNot: 'filters.negatable.isNot',
  empty: 'filters.isEmpty',
  notEmpty: 'filters.isNotEmpty',
}

const MULTI_SELECT_OPERATORS: MrtFilterOperator[] = ['is', 'isNot', 'empty', 'notEmpty']

const isFilterOperator = (value: unknown): value is MrtFilterOperator =>
  typeof value === 'string' && (MULTI_SELECT_OPERATORS as string[]).includes(value)

// Only accept the wrapped shape when it carries a known operator — persisted state with an unexpected
// operator falls back to the "is" default instead of flowing an invalid key into the intl lookups.
const isStoredFilterValue = (stored: unknown): stored is StoredFilterValue =>
  typeof stored === 'object' &&
  stored !== null &&
  !Array.isArray(stored) &&
  'operator' in stored &&
  isFilterOperator((stored as { operator: unknown }).operator)

// Filters persisted before operators existed stored the raw value directly. Normalise those to the
// wrapped shape (defaulting to "is") so saved table state keeps working without a reset.
export const readStoredFilterValue = (stored: unknown): StoredFilterValue =>
  isStoredFilterValue(stored) ? stored : { operator: DEFAULT_FILTER_OPERATOR, value: stored }

const NEGATIVE_FILTER_OPERATORS: MrtFilterOperator[] = ['isNot', 'empty']

// The chip is coloured by polarity so a combination of positive and negative filters can be read at a
// glance: "is not"/"is empty" exclude rows, while "is"/"is not empty" include them.
export const isNegativeFilterOperator = (operator: MrtFilterOperator): boolean =>
  NEGATIVE_FILTER_OPERATORS.includes(operator)

// "empty"/"notEmpty" filter on presence alone, so they carry no value; "is"/"isNot" need one.
export const operatorNeedsValue = (operator: MrtFilterOperator): boolean => operator === 'is' || operator === 'isNot'

// "isNot" is set membership (In/NotIn) and only applies to multi-select; every variant supports the
// presence operators, except 'custom' which owns its own value shape and so only offers "is" unless the
// column says otherwise.
export const getAvailableOperators = (
  variant: MrtColumnFilterVariant,
  columnOperators?: MrtFilterOperator[],
): MrtFilterOperator[] => {
  if (columnOperators?.length) return columnOperators
  if (variant === 'multi-select') return MULTI_SELECT_OPERATORS
  if (variant === 'custom') return ['is']
  if (variant === 'multi-step') return ['is', 'isNot']
  return ['is', 'empty', 'notEmpty']
}
